import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import AssetCard from "@/components/AssetCard";
import EditAssetModal from "@/components/EditAssetModal";
import { useAuth } from "@/context/AuthContext";
import useAutoRefresh from "@/lib/useAutoRefresh";
import { ArrowLeft, Download, Copy, Clock, Pencil, Trash2, ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ConfirmDialog";
import ShareAssetModal from "@/components/ShareAssetModal";
import { downloadAssetFile } from "@/lib/downloadAsset";

export default function AssetDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [asset, setAsset] = useState(null);
  const [related, setRelated] = useState([]);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [pageIdx, setPageIdx] = useState(0);
  const canManage = user && (user.role === "admin");
  // Viewers cannot download artwork
  const canDownload = !(user && user.role === "viewer" && asset && (asset.category || "").toLowerCase() === "artwork");

  useEffect(() => {
    setPageIdx(0);
    api.get(`/assets/${id}`).then((r) => setAsset(r.data));
    api.get(`/assets/${id}/related`).then((r) => setRelated(r.data));
  }, [id]);

  // Live-sync: refresh asset + related on focus / every 30s so admin edits appear
  // instantly for partners without a hard refresh. Skip while the edit modal is open
  // so we don't blow away in-flight form state.
  const refresh = useCallback(() => {
    if (!id) return;
    api.get(`/assets/${id}`).then((r) => setAsset(r.data)).catch(() => {});
    api.get(`/assets/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
  }, [id]);
  useAutoRefresh(refresh, { enabled: !editing });

  // If a legacy PDF is being backfilled server-side, poll for the pages to appear.
  useEffect(() => {
    if (!asset) return;
    if (asset.file_type !== "pdf") return;
    if ((asset.preview_pages || []).length > 0) return;
    let attempts = 0;
    const timer = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/assets/${id}`);
        if ((data.preview_pages || []).length > 0) {
          setAsset(data);
          clearInterval(timer);
        }
      } catch { /* ignore */ }
      if (attempts >= 6) clearInterval(timer);
    }, 6000);
    return () => clearInterval(timer);
  }, [asset, id]);

  // Keyboard arrow nav
  useEffect(() => {
    const handler = (e) => {
      const pages = asset?.preview_pages || [];
      if (pages.length < 2) return;
      if (e.key === "ArrowLeft") setPageIdx((i) => (i - 1 + pages.length) % pages.length);
      if (e.key === "ArrowRight") setPageIdx((i) => (i + 1) % pages.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [asset]);

  if (!asset) return <div className="text-sm text-muted-foreground animate-pulse">Loading…</div>;

  const pages = asset.preview_pages && asset.preview_pages.length > 0
    ? asset.preview_pages
    : (asset.preview_url ? [asset.preview_url] : []);
  const hasMulti = pages.length > 1;
  const currentPreview = pages[pageIdx] || asset.preview_url;
  const resolvedPreview = currentPreview && !currentPreview.startsWith("http")
    ? `${process.env.REACT_APP_BACKEND_URL}${currentPreview}`
    : currentPreview;

  async function download(fmt) {
    await downloadAssetFile(asset, fmt || asset.file_type);
  }

  // Build the list of format buttons the user actually sees. We only advertise
  // targets we can convert to server-side so nothing 500s at the click.
  function availableFormats() {
    const src = (asset.file_type || "").toLowerCase();
    const IMG = new Set(["jpg", "jpeg", "png", "webp", "heic", "heif", "gif", "bmp", "tiff", "tif"]);
    const out = new Set([src]);
    if (IMG.has(src)) {
      ["jpg", "png", "webp", "pdf"].forEach((f) => out.add(f));
    } else if (src === "pdf") {
      ["pdf", "jpg", "png"].forEach((f) => out.add(f));
    }
    // Preserve any explicitly-listed formats the admin curated (e.g. svg/eps
    // for logos) even though we can't convert to them — clicking them still
    // serves the original file, which is the desired behavior.
    (asset.formats || []).forEach((f) => out.add((f || "").toLowerCase()));
    return Array.from(out).filter(Boolean);
  }

  function copyLink() {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied");
  }

  async function handleDelete() {
    const ok = await confirmDialog({
      title: `Delete "${asset.title}"?`,
      description: "This cannot be undone.",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/assets/${asset.id}`);
      toast.success("Asset deleted");
      nav("/library");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-10 slide-up" data-testid={`asset-detail-${id}`}>
      <button
        type="button"
        onClick={() => {
          // Prefer the explicit "from" state set by AssetCard/Dashboard links
          const from = location.state && location.state.from;
          if (from && typeof from === "string" && from !== location.pathname) {
            nav(from);
            return;
          }
          // Fallback: navigate to the asset's category page (product/strain/etc.)
          const cat = (asset.category || "").toLowerCase();
          const catRoute = {
            product: "/products",
            strain: "/strains",
            menu: "/retail",
            marketing: "/marketing",
            retail: "/retail",
            sales: "/sales",
            artwork: "/artwork",
          }[cat];
          if (catRoute) nav(catRoute);
          else nav("/library");
        }}
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition"
        data-testid="asset-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      <section className="grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3 space-y-3">
          <div className="relative aspect-[4/3] rounded-sm border border-border bg-muted overflow-hidden group">
            {resolvedPreview ? (
              <img src={resolvedPreview} alt={`${asset.title} — page ${pageIdx + 1}`} className="w-full h-full object-contain" data-testid="asset-preview-image" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground font-mono text-xs uppercase tracking-widest">No preview</div>
            )}

            {hasMulti && (
              <>
                <button
                  onClick={() => setPageIdx((i) => (i - 1 + pages.length) % pages.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-black/80 transition"
                  data-testid="asset-preview-prev"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setPageIdx((i) => (i + 1) % pages.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-black/80 transition"
                  data-testid="asset-preview-next"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 backdrop-blur border border-white/10 text-white font-mono text-[10px] uppercase tracking-[0.3em]" data-testid="asset-preview-counter">
                  {pageIdx + 1} / {pages.length}
                </div>
              </>
            )}
          </div>

          {hasMulti && (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="asset-preview-thumbs">
              {pages.map((p, i) => {
                const src = p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`;
                return (
                  <button
                    key={p}
                    onClick={() => setPageIdx(i)}
                    className={`relative shrink-0 w-16 h-20 rounded-sm border overflow-hidden transition ${
                      i === pageIdx ? "border-foreground shadow-[0_0_0_2px_var(--foreground)]" : "border-border opacity-60 hover:opacity-100"
                    }`}
                    data-testid={`asset-preview-thumb-${i}`}
                    aria-label={`Go to page ${i + 1}`}
                  >
                    <img src={src} alt={`page ${i + 1}`} className="w-full h-full object-cover" />
                    <span className="absolute bottom-0 inset-x-0 bg-black/70 text-white font-mono text-[9px] text-center py-0.5">{i + 1}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">{asset.category} · {asset.subcategory}</div>
            <h1 className="font-display text-3xl md:text-4xl uppercase font-bold tracking-tight">{asset.title}</h1>
            {asset.description && <p className="text-sm text-muted-foreground mt-3 whitespace-pre-line">{asset.description}</p>}
            {asset.tags?.length > 0 && (
              <div className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Keywords</div>
                <div className="flex flex-wrap gap-1.5">
                  {asset.tags.map((t) => (
                    <span key={t} className="px-2 py-0.5 text-[11px] border border-border rounded-full">{t}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <Meta label="File Type" value={asset.file_type?.toUpperCase()} />
            <Meta label="Version" value={`v${asset.version}`} />
            {canManage && <Meta label="Downloads" value={asset.downloads || 0} />}
            <Meta label="Views" value={asset.views || 0} />
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Download Formats</div>
            <div className="grid grid-cols-3 gap-2">
              {availableFormats().map((f) => (
                <button
                  key={f}
                  onClick={() => download(f)}
                  disabled={!canDownload}
                  className="border border-border rounded-sm py-2.5 uppercase text-xs font-mono tracking-widest hover:bg-foreground hover:text-background transition disabled:opacity-40 disabled:pointer-events-none"
                  data-testid={`download-format-${f}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className={`grid ${canDownload ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
            {canDownload && (
              <button onClick={() => download(asset.file_type)} className="bg-foreground text-background py-3 uppercase text-xs font-semibold tracking-widest flex items-center justify-center gap-2" data-testid="asset-download-btn">
                <Download className="h-3.5 w-3.5" /> Download
              </button>
            )}
            <button onClick={copyLink} className="border border-border py-3 uppercase text-xs font-semibold tracking-widest flex items-center justify-center gap-2 hover:bg-surface-elev transition" data-testid="asset-copy">
              <Copy className="h-3.5 w-3.5" /> Copy Link
            </button>
          </div>

          {canManage && (
            <button
              onClick={() => setSharing(true)}
              className="w-full border py-3 uppercase text-xs font-semibold tracking-widest flex items-center justify-center gap-2 transition"
              style={{ borderColor: "#FBC90C", color: "#FBC90C" }}
              data-testid="asset-share-btn"
            >
              <Share2 className="h-3.5 w-3.5" /> Share Public Link
            </button>
          )}

          {canManage && (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setEditing(true)} className="border border-border py-3 uppercase text-xs font-semibold tracking-widest flex items-center justify-center gap-2 hover:bg-surface-elev transition" data-testid="asset-edit-btn">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
              <button onClick={handleDelete} className="border py-3 uppercase text-xs font-semibold tracking-widest flex items-center justify-center gap-2 hover:opacity-90 transition text-white" style={{ background: "#E15C2C", borderColor: "#E15C2C" }} data-testid="asset-delete-btn">
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>
          )}

          {asset.states?.length > 0 && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">State Availability</div>
              <div className="flex flex-wrap gap-1.5">
                {asset.states.map((s) => (
                  <span key={s} className="px-2 py-0.5 text-[11px] font-mono uppercase tracking-widest bg-surface-elev rounded-sm">{s}</span>
                ))}
              </div>
            </div>
          )}

          <div className="border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
              <Clock className="h-3 w-3" /> History
            </div>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span>Created</span><span className="text-muted-foreground">{new Date(asset.created_at).toLocaleDateString()}</span></div>
              <div className="flex justify-between"><span>Last updated</span><span className="text-muted-foreground">{new Date(asset.updated_at).toLocaleDateString()}</span></div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1">Also see</div>
          <h2 className="font-display text-2xl uppercase font-semibold tracking-tight mb-4">Related Assets</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((a) => <AssetCard key={a.id} asset={a} />)}
          </div>
        </section>
      )}

      {canManage && (
        <>
          <EditAssetModal
            open={editing}
            asset={asset}
            onClose={() => setEditing(false)}
            onSaved={(updated) => { setAsset(updated); }}
          />
          <ShareAssetModal
            open={sharing}
            asset={asset}
            onClose={() => setSharing(false)}
          />
        </>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="border border-border rounded-sm p-3">
      <div className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold mt-1">{value}</div>
    </div>
  );
}
